import { Button } from "@jugl-web/ui-kit";
import { PlainButton, SidebarDrawer, Tooltip } from "@jugl-web/ui-components";
import { TextAreaInput } from "@jugl-web/ui-components/cross-platform/forms/TextAreaInput";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import React, { useCallback, useEffect, useState } from "react";
import { ReactComponent as QuestionIcon } from "./assets/question.svg";
import { ReactComponent as QuestionMarkIcon } from "./assets/question-mark.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { useAgentKnowledge } from "../../../../../../hooks/useAgentKnowledge";

export const ReviseAnswerSidebar: React.FC<{
  isOpen: boolean;
  onRequestClose: () => void;
  entityId: string;
  question: string;
  agentAnswer: string;
}> = ({ isOpen, onRequestClose, question, agentAnswer, entityId }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { aiWorkflowsApi } = useRestApiProvider();
  const { handleCXApiError } = useAgentKnowledge(entityId);
  const [expectedAnswer, setExpectedAnswer] = useState("");

  const [uploadQuestionKnowledge, { isLoading }] =
    aiWorkflowsApi.useUploadQuestionKnowledgeMutation();

  const handleCreateQuestionKnowledge = useCallback(async () => {
    try {
      await uploadQuestionKnowledge({
        agentId: entityId,
        entityId,
        content: [{ question, answer: expectedAnswer.trim() }],
      }).unwrap();

      toast(
        t({
          id: "cx-ai-agents-page.toast.new-qna-added",
          defaultMessage: "New Q&A was added to the Knowledge",
        })
      );
      onRequestClose();
    } catch (error) {
      handleCXApiError(error);
    }
  }, [
    entityId,
    expectedAnswer,
    handleCXApiError,
    onRequestClose,
    question,
    t,
    toast,
    uploadQuestionKnowledge,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setExpectedAnswer("");
    }
  }, [isOpen]);

  return (
    <SidebarDrawer
      isOpen={isOpen}
      onClose={onRequestClose}
      title={
        <div className="flex items-center gap-3">
          <QuestionIcon />
          <span>
            {t({
              id: "cx-ai-agents-page.revise-answer",
              defaultMessage: "Revise Answer",
            })}
          </span>
        </div>
      }
      className="w-[500px]"
    >
      <SidebarDrawer.Content className="flex flex-col gap-8 p-8">
        <div className="flex items-center justify-between gap-3">
          <span className="font-secondary font-medium text-black">
            {t({
              id: "cx-ai-agents-page.new-q&a",
              defaultMessage: "📝 New Q&A",
            })}
          </span>
          <Tooltip
            placement="bottom-end"
            renderTrigger={({ props, ref }) => (
              <PlainButton
                ref={ref}
                {...props}
                className="bg-grey-100 hover:bg-grey-200 flex h-8 w-8 items-center justify-center rounded-full transition-colors"
              >
                <QuestionMarkIcon />
              </PlainButton>
            )}
          >
            {t({
              id: "cx-ai-agents-page.revised-answer-tooltip",
              defaultMessage:
                "Each revised answer is added to Knowledge as new Q&A item. Q&A’s help AI Agent to give 100% correct answers to  specific questions 🙌",
            })}
          </Tooltip>
        </div>

        <div className="flex flex-col gap-6">
          <TextAreaInput
            labelPosition="border-overlap"
            label={t({
              id: "cx-ai-agents-page.question",
              defaultMessage: "Question",
            })}
            value={question}
            isDisabled
            classNames={{
              inputClassName: "text-sm",
            }}
          />

          <TextAreaInput
            labelPosition="border-overlap"
            label={t({
              id: "cx-ai-agents-page.agent-answer",
              defaultMessage: "Agent Answer",
            })}
            value={agentAnswer}
            isDisabled
            classNames={{
              inputClassName: "text-sm",
            }}
          />

          <TextAreaInput
            labelPosition="border-overlap"
            label={t({
              id: "cx-ai-agents-page.expected-answer",
              defaultMessage: "Expected Answer",
            })}
            value={expectedAnswer}
            onChange={(e) => setExpectedAnswer(e.target.value.trimStart())}
            classNames={{
              inputClassName: "min-h-[250px] text-sm",
            }}
          />
          <div className="flex items-center gap-4">
            <Button
              variant="tertiary"
              onClick={onRequestClose}
              className="w-[140px]"
              isDisabled={isLoading}
            >
              {t({ id: "common.cancel", defaultMessage: "Cancel" })}
            </Button>
            <Button
              className="grow"
              onClick={handleCreateQuestionKnowledge}
              isDisabled={!expectedAnswer.trim() || isLoading}
              iconEnd={<ArrowIcon />}
            >
              {t({
                id: "cx-ai-agents-page.revise-answer",
                defaultMessage: "Revise Answer",
              })}
            </Button>
          </div>
        </div>
      </SidebarDrawer.Content>
    </SidebarDrawer>
  );
};
